import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { CheckCircle2, XCircle, FileSignature } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const fmtMoney = (n, currency = 'AUD') => new Intl.NumberFormat('en-AU', { style: 'currency', currency }).format(n || 0);

export default function PublicQuote() {
  const { token } = useParams();
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(true);
  const [acceptForm, setAcceptForm] = useState({ name: '', email: '' });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    axios.get(`${API}/share/quote/${token}`)
      .then((r) => setQuote(r.data))
      .catch(() => toast.error('Quote not found or no longer available'))
      .finally(() => setLoading(false));
  }, [token]);

  const accept = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/share/quote/${token}/accept`, acceptForm);
      const r = await axios.get(`${API}/share/quote/${token}`);
      setQuote(r.data);
      toast.success('Thank you — quote accepted');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not accept');
    } finally {
      setBusy(false);
    }
  };

  const decline = async () => {
    if (!window.confirm('Decline this quote?')) return;
    setBusy(true);
    try {
      await axios.post(`${API}/share/quote/${token}/decline`);
      const r = await axios.get(`${API}/share/quote/${token}`);
      setQuote(r.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not decline');
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <div className="min-h-screen bg-slate-50 flex items-center justify-center"><p className="text-slate-500">Loading…</p></div>;
  if (!quote) return <div className="min-h-screen bg-slate-50 flex items-center justify-center"><p className="text-slate-500">Quote not available.</p></div>;

  const isFinal = ['accepted', 'declined', 'converted'].includes(quote.status);

  return (
    <div className="min-h-screen bg-slate-50 py-10 px-4">
      <div className="max-w-3xl mx-auto">
        <div className="flex items-center gap-3 mb-6">
          <FileSignature className="w-8 h-8 text-emerald-600" />
          <div>
            <p className="text-xs text-slate-500">Quote · {quote.number}</p>
            <h1 className="text-2xl font-bold text-slate-900">{quote.title || 'Quotation'}</h1>
          </div>
        </div>

        <Card className="glass-card p-6 mb-4">
          {quote.client && <p className="text-sm text-slate-600 mb-3"><b>To:</b> {quote.client.name} {quote.client.contact_name && <>· {quote.client.contact_name}</>}</p>}
          {quote.intro_html && <div className="prose prose-sm max-w-none mb-4" dangerouslySetInnerHTML={{ __html: quote.intro_html }} />}

          <table className="w-full text-sm mt-4">
            <thead className="text-left text-xs text-slate-500 border-b">
              <tr><th className="py-2">Description</th><th className="py-2 w-20">Qty</th><th className="py-2 w-20">Unit</th><th className="py-2 w-28">Price</th><th className="py-2 w-28 text-right">Total</th></tr>
            </thead>
            <tbody>
              {(quote.items || []).map((it) => (
                <tr key={it.id} className="border-b last:border-0">
                  <td className="py-2">{it.description}</td>
                  <td className="py-2">{it.quantity}</td>
                  <td className="py-2 text-slate-500">{it.unit}</td>
                  <td className="py-2">{fmtMoney(it.unit_price, quote.currency)}</td>
                  <td className="py-2 text-right font-medium">{fmtMoney((it.quantity || 0) * (it.unit_price || 0), quote.currency)}</td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className="mt-4 ml-auto w-fit text-sm space-y-1">
            <div className="flex justify-between gap-8"><span className="text-slate-500">Subtotal:</span><span>{fmtMoney(quote.subtotal, quote.currency)}</span></div>
            {quote.discount > 0 && <div className="flex justify-between gap-8"><span className="text-slate-500">Discount:</span><span>−{fmtMoney(quote.discount, quote.currency)}</span></div>}
            <div className="flex justify-between gap-8"><span className="text-slate-500">Tax ({(quote.tax_rate * 100).toFixed(0)}%):</span><span>{fmtMoney(quote.tax, quote.currency)}</span></div>
            <div className="flex justify-between gap-8 text-lg pt-2 border-t"><span className="font-semibold">Total:</span><span className="font-bold">{fmtMoney(quote.total, quote.currency)}</span></div>
          </div>

          {quote.valid_until && <p className="text-xs text-slate-500 mt-4">Valid until: {quote.valid_until}</p>}
        </Card>

        {isFinal ? (
          <Card className="glass-card p-6 text-center">
            {quote.status === 'accepted' && <p className="text-emerald-700 font-medium flex items-center justify-center gap-2"><CheckCircle2 className="w-5 h-5" /> Quote accepted</p>}
            {quote.status === 'declined' && <p className="text-rose-700 font-medium flex items-center justify-center gap-2"><XCircle className="w-5 h-5" /> Quote declined</p>}
            {quote.status === 'converted' && <p className="text-emerald-700 font-medium flex items-center justify-center gap-2"><CheckCircle2 className="w-5 h-5" /> Accepted & invoiced</p>}
          </Card>
        ) : (
          <Card className="glass-card p-6">
            <h2 className="font-semibold mb-3">Accept this quote</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
              <div><Label>Your name</Label><Input value={acceptForm.name} onChange={(e) => setAcceptForm({ ...acceptForm, name: e.target.value })} /></div>
              <div><Label>Email</Label><Input type="email" value={acceptForm.email} onChange={(e) => setAcceptForm({ ...acceptForm, email: e.target.value })} /></div>
            </div>
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={decline} disabled={busy}>Decline</Button>
              <Button onClick={accept} disabled={busy}>{busy ? '…' : 'Accept'}</Button>
            </div>
          </Card>
        )}
      </div>
    </div>
  );
}
